import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Home,
  LocalAtm,
  AccountBalance,
  ShowChart,
  Search,
  Visibility,
  Favorite,
  FavoriteBorder,
  FilterList,
  Refresh,
  Business
} from '@mui/icons-material';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

// Import services and context
import { useFavorites } from '../../../context/FavoritesContext';

// Enhanced Metric Card Component with proper theming
const MetricCard = ({ title, value, icon, change, color = 'primary', subtitle, trend }) => {
  const theme = useTheme();
  const isPositive = change > 0;
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        background: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 1, fontWeight: 500, letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              color="text.primary"
              sx={{ mb: 0.5, lineHeight: 1.2 }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              width: 48,
              height: 48
            }}
          >
            {icon}
          </Avatar>
        </Stack>
        
        {change !== undefined && (
          <Stack direction="row" alignItems="center" spacing={1}>
            {isPositive ? (
              <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
            ) : (
              <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
            )}
            <Typography 
              variant="body2" 
              color={isPositive ? 'success.main' : 'error.main'}
              fontWeight="medium"
            >
              {isPositive ? '+' : ''}{change}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              vs last month
            </Typography>
          </Stack>
        )}
        
        {trend && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={trend} 
              sx={{ 
                height: 4, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 2
                }
              }} 
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Chart Container Component
const ChartContainer = ({ title, subtitle, children, action, height = 300 }) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 2
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        <Box sx={{ width: '100%', height }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
};

// Property Data Table Component (Simplified)
const PropertyTable = () => {
  const theme = useTheme();
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();

  const fetchProperties = React.useCallback(async () => {
    try {
      // Mock data for demo
      const mockData = Array.from({ length: 50 }, (_, index) => ({
        id: index + 1,
        address: `${123 + index} Main St`,
        city: ['Camden', 'Burlington', 'Gloucester', 'Atlantic', 'Mercer'][index % 5],
        state: 'NJ',
        minimum_bid: `$${(250000 + Math.random() * 200000).toFixed(0)}`,
        estimated_value: `$${(300000 + Math.random() * 300000).toFixed(0)}`,
        property_type: ['Single Family', 'Townhouse', 'Condo', 'Multi-Family'][index % 4]
      }));
      
      setProperties(mockData.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isFavorite = (propertyId) => {
    return favorites.some(fav => fav.id === propertyId);
  };

  const toggleFavorite = (property) => {
    if (isFavorite(property.id)) {
      removeFromFavorites(property.id);
    } else {
      addToFavorites(property);
    }
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 2
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Recent Properties
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Latest foreclosure opportunities
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Search properties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
              sx={{ width: 200 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              size="small"
            >
              Filter
            </Button>
          </Stack>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Min Bid</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Est. Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => (
                <TableRow key={property.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                        <Home sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {property.address}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {property.city}, {property.state}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={property.property_type}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {property.minimum_bid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {property.estimated_value}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isFavorite(property.id) ? "Remove from favorites" : "Add to favorites"}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleFavorite(property)}
                          color={isFavorite(property.id) ? "error" : "default"}
                        >
                          {isFavorite(property.id) ? 
                            <Favorite sx={{ fontSize: 16 }} /> : 
                            <FavoriteBorder sx={{ fontSize: 16 }} />
                          }
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={50}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
};

// Main Content Component
const ProductionDashboardContent = ({ dashboardData, onRefresh }) => {
  const theme = useTheme();
  const { favorites } = useFavorites();

  // Prepare chart data
  const marketTrendsData = dashboardData?.marketTrends || [];
  const propertyTypesData = dashboardData?.propertyTypes?.map(type => ({
    name: type.name,
    value: type.count,
    percentage: type.value
  })) || [];

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    alpha(theme.palette.primary.main, 0.7),
    alpha(theme.palette.secondary.main, 0.7)
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              Property Portfolio Overview
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Your comprehensive investment summary and insights
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<Refresh />}
            onClick={onRefresh}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            Refresh Data
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Total Properties"
            value={dashboardData?.overview?.totalProperties?.toLocaleString() || '0'}
            icon={<Home />}
            change={12.5}
            subtitle="Active listings"
            trend={75}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Average Value"
            value={`$${(dashboardData?.overview?.avgPrice || 0).toLocaleString()}`}
            icon={<LocalAtm />}
            change={8.3}
            subtitle="Per property"
            trend={65}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Portfolio Value"
            value={`$${((dashboardData?.overview?.portfolioValue || 0) / 1000000).toFixed(1)}M`}
            icon={<AccountBalance />}
            change={15.7}
            subtitle="Total estimated"
            trend={85}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Favorites"
            value={favorites.length.toString()}
            icon={<Favorite />}
            change={5.2}
            subtitle="Saved properties"
            trend={45}
          />
        </Grid>

        {/* Charts Row */}
        <Grid item xs={12} lg={8}>
          <ChartContainer
            title="Market Performance"
            subtitle="6-month property price trends"
            action={
              <Button size="small" startIcon={<ShowChart />}>
                View Details
              </Button>
            }
            height={280}
          >
            <ResponsiveContainer>
              <AreaChart data={marketTrendsData}>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                <XAxis dataKey="month" stroke={theme.palette.text.secondary} fontSize={12} />
                <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    boxShadow: theme.shadows[4]
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="avgPrice" 
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartContainer
            title="Property Types"
            subtitle="Portfolio distribution"
            height={280}
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={propertyTypesData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {propertyTypesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>

        {/* Properties Table */}
        <Grid item xs={12}>
          <PropertyTable />
        </Grid>

        {/* Activity and Stats Row */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              borderRadius: 2,
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                Recent Activity
              </Typography>
              <Stack spacing={2}>
                {(dashboardData?.recentActivity || []).slice(0, 5).map((activity, index) => (
                  <Stack key={index} direction="row" alignItems="center" spacing={2}>
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: activity.type === 'favorite' ? 'error.light' : 
                                 activity.type === 'view' ? 'info.light' : 'primary.light'
                      }}
                    >
                      {activity.type === 'favorite' ? <Favorite sx={{ fontSize: 16 }} /> :
                       activity.type === 'view' ? <Visibility sx={{ fontSize: 16 }} /> :
                       <Business sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {activity.action}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {activity.property}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {activity.time}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              borderRadius: 2,
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                Quick Stats
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Properties Viewed
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {dashboardData?.overview?.recentlyViewed || 0}
                    </Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }} 
                  />
                </Box>
                
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Active Watching
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {dashboardData?.overview?.activeWatching || 0}
                    </Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={45} 
                    color="secondary"
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1)
                    }} 
                  />
                </Box>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Expected ROI
                  </Typography>
                  <Chip
                    label={`${dashboardData?.overview?.avgProfitMargin || 18.5}%`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductionDashboardContent;



